import React, { useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import useHdyhOptions from '~/acquisition/utils/useHdyhOptions';
import { isYitty } from '~/shared/utils/brandNameHelper';
import useHasFeature, { featureNames } from '~/shared/utils/useHasFeature';
import { mobile } from '~/techstyle-shared/react-components';
import { useIntl, FormattedMessage } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

import {
  SelectorDropdownButton,
  QuizDropdown,
  QuizDropdownOption,
  QuizDropdownList,
  QuizNativeSelect,
} from '../../components/QuizFormComponents';
import TieredListDropdown from '../TieredListDropdown/TieredListDropdown';

const StyledQuizNativeSelectWrapper = styled.div`
  ${({ theme: { colors } }) => css`
    border: 1px solid ${colors.border};
    border-radius: ${({ theme }) => theme.borderRadiusInput};
  `}

  ${({ src, theme: { colors } }) =>
    src === 'SignupForm' &&
    `
          border-radius: 0;
          padding: 3px 0 3px 6px;
          background: ${colors.palette.white};
        `}

  ${({ src }) =>
    src === 'SpeedySignupForm' &&
    `
          border: 0;
          padding-left: 8px;
          flex: 1;

          #quiz-native-select label {
            font-size: 14px;
          }
        `}
`;

const StyledQuizNativeSelect = styled(QuizNativeSelect)`
  ${({ src }) =>
    src === 'SkippedRegistration'
      ? `
          ${({ theme }) => theme.quiz.skippedRegSelect}
        `
      : ''}
  border: none;
`;

const referrerButtonStyles = css`
  ${({ theme }) => theme.quiz.quizSizingReferrerButton}
`;

const HowDidYouHearDropdownWrapper = styled.div`
  ${({ theme }) => theme.quiz.HowDidYouHearDropdownWrapper}
  ${mobile`
    ${({ $isSkippedQuiz }) =>
      $isSkippedQuiz
        ? css`
            min-height: 56px;
          `
        : css`
            border-radius: 8px;
            border-top-right-radius: 0px;
            border-top-left-radius: 0px;
            min-height: 62px;
          `}
  `}
`;

const NativeHDYHDropdown = ({
  values,
  errors,
  setFieldValue,
  setErrors,
  handleFormUpdate,
  additionalNativeSelectStyles,
  className,
  src,
  $isSkippedQuiz,
}) => {
  const intl = useIntl();
  const { tld } = useDomain();
  const isUSDomain = tld === '.com';
  const options = useHdyhOptions();

  const dropdownOptions = useMemo(
    () =>
      options.map(({ referrerId, label }) => ({
        value: referrerId.toString(),
        label,
      })),
    [options]
  );

  return (
    <StyledQuizNativeSelectWrapper src={src}>
      <StyledQuizNativeSelect
        id="hdyh-quiz-dropdown-native-select"
        data-autotag="quiz_how_you_hear_about"
        name="referrer"
        handleFormUpdate={handleFormUpdate}
        additionalStyles={additionalNativeSelectStyles}
        className={className}
        label={intl.formatMessage({
          id: 'quiz.sizing_screen_HDYH_header',
          defaultMessage: 'How did you hear about us?*',
        })}
        errors={errors}
        setErrors={setErrors}
        setFieldValue={setFieldValue}
        showZip={isUSDomain}
        options={dropdownOptions}
        $isSkippedQuiz={$isSkippedQuiz}
        values={values}
      />
    </StyledQuizNativeSelectWrapper>
  );
};

const YittyHDYHDropdown = ({ values, errors, handleChange }) => {
  const options = useHdyhOptions();

  const dropdownOptions = useMemo(
    () =>
      options.map(({ referrerId, label }) => ({
        value: referrerId.toString(),
        label,
      })),
    [options]
  );

  return (
    <QuizDropdown
      id="yitty-quiz-referrer"
      value={values.referrer}
      options={dropdownOptions}
      onChange={handleChange}
      data-autotag="quiz_how_you_hear_about"
    >
      <SelectorDropdownButton
        currentSelection={values.referrer}
        hasError={!!errors.referrer}
        errors={errors}
        dataAutotag="quiz-referrer"
        dropDownMessage={
          <FormattedMessage
            id="quiz.sizing_screen_HDYH_header"
            defaultMessage="How did you hear about us?*"
          />
        }
        buttonStyles={referrerButtonStyles}
      />
      <QuizDropdownList>
        {dropdownOptions.map(({ value, label }) => (
          <QuizDropdownOption
            key={value}
            value={value}
            data-selected={value === values.referrer?.value}
          >
            {label}
          </QuizDropdownOption>
        ))}
      </QuizDropdownList>
    </QuizDropdown>
  );
};

YittyHDYHDropdown.propTypes = {
  errors: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
};

const HowDidYouHearDropdown = ({
  values,
  errors,
  setFieldValue,
  setErrors,
  handleFormUpdate,
  additionalNativeSelectStyles,
  className,
  src,
  $isSkippedQuiz = false,
  hasError = false,
}) => {
  const { tld } = useDomain();
  const isUSDomain = tld === '.com';
  const { enabled: isTieredHDYHFeatureEnabled } = useHasFeature(
    featureNames.HDYH_TIERED_LIST
  );
  const isTieredHDYHEnabled = isTieredHDYHFeatureEnabled && isUSDomain;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleChange = (option) => {
    const referrer = option || { value: '', label: '' };
    setFieldValue('referrer', referrer);
    setErrors({ ...errors, referrer: '' });
    /*
      'handleFormUpdate' is responsible for updating additional states or triggering side effects when form values change.
      It doesn't directly update Formik's internal state but relies on setFieldValue to do so.
    */
    handleFormUpdate({
      ...values,
      referrer: option || { value: '', label: '' },
    }); // calls UpdateAnswer
  };

  if (isTieredHDYHEnabled) {
    return (
      <HowDidYouHearDropdownWrapper>
        <TieredListDropdown
          values={values}
          errors={errors}
          handleChange={handleChange}
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
          hasError={hasError}
        />
      </HowDidYouHearDropdownWrapper>
    );
  }

  if (isYitty()) {
    return (
      <HowDidYouHearDropdownWrapper>
        <YittyHDYHDropdown
          values={values}
          errors={errors}
          handleChange={handleChange}
        />
      </HowDidYouHearDropdownWrapper>
    );
  }

  return (
    <HowDidYouHearDropdownWrapper $isSkippedQuiz={$isSkippedQuiz}>
      <NativeHDYHDropdown
        values={values}
        errors={errors}
        setFieldValue={setFieldValue}
        setErrors={setErrors}
        handleFormUpdate={handleFormUpdate}
        additionalNativeSelectStyles={additionalNativeSelectStyles}
        className={className}
        src={src}
        $isSkippedQuiz={$isSkippedQuiz}
        hasError={hasError}
      />
    </HowDidYouHearDropdownWrapper>
  );
};

NativeHDYHDropdown.propTypes = {
  $isSkippedQuiz: PropTypes.bool,
  additionalNativeSelectStyles: PropTypes.object,
  className: PropTypes.string,
  errors: PropTypes.object.isRequired,
  handleFormUpdate: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  src: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
};

HowDidYouHearDropdown.propTypes = {
  $isSkippedQuiz: PropTypes.bool,
  additionalNativeSelectStyles: PropTypes.object,
  className: PropTypes.string,
  errors: PropTypes.object.isRequired,
  handleFormUpdate: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  setErrors: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  src: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
};

export default HowDidYouHearDropdown;
